<template>
  <div class="analytics-table-item">
    <transaction-table-item
      ref="transactionItem"
      :transaction="transaction"
      @actionFired="handleActionFired"
      :details-blocks="transaction.TRANSACTION_DETAIL_BLOCKS"
      :full-info-actions="additionalItemActions"
      @fullInfoLoaded="handleFullInfo"
      @parentOperationIdClicked="handleParentOperationIdClicked"
    >
      <template
        #shortInfo="{
          hideActionsMenu,
          toggleActionsMenu,
          isShowActionsMenu,
          makeAction,
          actionsPending,
        }"
      >
        <div class="transaction-table-item__col transaction-table-item__date">
          <div class="transaction-table-item__date-main">
            {{ transaction.transactionDate }}
          </div>
          <div class="transaction-table-item__date-time">
            {{ transaction.transactionTime }}
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__type">
          {{ $t(`transaction-types.${transaction.transactionType || 'none'}`) }}

          <div
            class="transaction-table-item__type-refund"
            v-if="transaction.operationTypeId === $options.refundOperationId"
          >
            <VDropdown trigger="hover" offset="10" placement="top">
              <svg
                width="21"
                height="16"
                viewBox="0 0 21 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="21" height="16" rx="8" fill="#E0E6F2" />
                <path
                  d="M7.7617 10.999L5.85693 9.09422L7.7617 7.18945"
                  stroke="#465A8B"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.48854 9.0947H11.2736C13.0224 9.0947 14.4284 7.62653 14.4284 5.8804V5.28516"
                  stroke="#465A8B"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <template v-slot:popover>
                <div class="bill-template__actions-item-hint">
                  {{ $t('transaction.hint.refund') }}
                </div>
              </template>
            </VDropdown>
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__pt-id">
          {{ transaction.partnerTransactionId }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__op-id">
          {{ transaction.operationId }}
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__order-number"
        >
          {{ transaction.orderNumber }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__client">
          <div class="transaction-table-item__client-full-name">
            {{ transaction.userFullName }}
          </div>
          <div class="transaction-table-item__client-phone">
            {{ transaction.userPhone }}
          </div>
          <div class="transaction-table-item__client-email">
            {{ transaction.userEmail }}
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__amount">
          {{ transaction.transactionAmount }}
          {{ transaction.transactionCurrencyValue }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__status">
          <transaction-status
            :status-id="transaction.operationStatus"
          ></transaction-status>
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__actions"
        >
          <div
            class="transaction-table-item__actions-btn"
            @click="toggleActionsMenu"
          >
            <svg
              width="20"
              height="4"
              viewBox="0 0 20 4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="2" cy="2" r="2" fill="#B9C4D8" />
              <circle cx="18" cy="2" r="2" fill="#B9C4D8" />
              <circle cx="10" cy="2" r="2" fill="#B9C4D8" />
            </svg>
          </div>
          <div
            class="transaction-table-item__actions-menu"
            v-if="isShowActionsMenu"
            v-click-outside="hideActionsMenu"
          >
            <template v-if="itemActions.length > 0">
              <template v-for="action in itemActions">
                <require-access
                  v-if="action.accessControl"
                  :action="action.accessControl.action"
                  :data="action.accessControl.data"
                >
                  <div
                    class="transaction-table-item__actions-menu-item"
                    :class="{ disabled: actionsPending.includes(action.id) }"
                    @click.stop="makeAction(action)"
                  >
                    {{ action.title }}
                  </div>
                </require-access>
                <div
                  class="transaction-table-item__actions-menu-item"
                  :class="{ disabled: actionsPending.includes(action.id) }"
                  @click.stop="makeAction(action)"
                  v-else
                >
                  {{ action.title }}
                </div>
              </template>
            </template>
            <template v-else>
              <div class="transaction-table-item__actions-no-actions">
                {{ $t('transaction-actions.no-available-actions') }}
              </div>
            </template>
          </div>
        </div>
      </template>
    </transaction-table-item>
    <transaction-receipt-popup
      :transaction="transaction"
      :pdf-blob="transactionReceiptBlob"
      ref="transactionReceiptPopup"
    ></transaction-receipt-popup>
    <callback-transaction-popup
      ref="callbackTransactionPopup"
      :is-active="showCallback"
      :callback-information="callbackInformation"
      @showCallback="showCallback = $event"
    ></callback-transaction-popup>
  </div>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../../utils/gogocodeTransfer'
import TransactionTableItem from '@vue-app/components/company/transactions/table/TransactionsTableItem.vue'
import { Transaction } from '@js-app/entities/transaction/Transaction'
import TransactionStatus from '@vue-app/components/transaction/TransactionStatus.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import TransactionReceiptPopup from '@vue-app/components/company/transactions/popup/TransactionReceiptPopup.vue'
import CallbackTransactionPopup from '@vue-app/components/company/transactions/popup/CallbackTransactionPopup.vue'
import { REFUND } from '@js-app/const/operation-types-ids'
import { mapState } from 'vuex'
import { SupportService } from '@js-app/services/support/support-service'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import CallbackOperationsService from '@js-app/services/callback-operations.service'

export default {
  name: 'AnalyticsTableItem',
  components: {
    RequireAccess,
    TransactionReceiptPopup,
    TransactionStatus,
    TransactionTableItem,
    CallbackTransactionPopup,
  },
  props: {
    transaction: {
      type: Transaction,
    },
  },
  data: () => ({
    transactionReceiptBlob: null,
    innerTransaction: null,
    showCallback: false,
    callbackInformation: [],
  }),
  computed: {
    ...mapState(['selectedCompany']),
    itemActions() {
      let _actions = []

      if (this.transaction.canGetReceipt) {
        _actions.push({
          id: 'get-receipt',
          title: this.$t('transaction-actions.get-receipt'),
          handler: this.loadReceipt,
          accessControl: {
            action: 'read',
            data: 'analitics-receipt',
          },
        })
      }

      _actions.push({
        id: 'callback',
        title: 'Callback',
        handler: this.loadCallback,
        accessControl: {
          action: 'read',
          data: 'analitics-receipt',
        },
      })

      _actions.push({
        id: 'support-tg-chat',
        title: this.$t('transaction-actions.support-tg-chat'),
        handler: SupportService.goToTelegramChat,
      })

      return _actions
    },
    additionalItemActions() {
      let _actions = []

      if (
        this.innerTransaction?.canMakeRefund ||
        this.innerTransaction?.canCancelPreAuth
      ) {
        _actions.push({
          id: 'refund',
          title: this.$t('transaction-actions.make-refund'),
          handler: this.makeRefund,
          icon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.6886 2.61804C11.6015 2.54433 11.4889 2.49988 11.3658 2.49988C11.2428 2.49988 11.1301 2.54433 11.043 2.61804H9.93776C9.66161 2.61804 9.43776 2.8419 9.43776 3.11804C9.43776 3.39418 9.66161 3.61804 9.93776 3.61804H10.8658V5.71428C10.8658 7.09576 9.51654 8.38476 7.67997 8.38476H2.71813L3.89904 7.38779C4.11004 7.20965 4.13668 6.89419 3.95854 6.68319C3.78041 6.47219 3.46495 6.44555 3.25395 6.62369L1.02853 8.50249C0.915998 8.59749 0.851074 8.73727 0.851074 8.88454C0.851074 9.03181 0.915998 9.17159 1.02853 9.26659L3.25395 11.1454C3.46495 11.3235 3.78041 11.2969 3.95854 11.0859C4.13668 10.8749 4.11004 10.5594 3.89904 10.3813L2.71866 9.38476H7.67997C9.92982 9.38476 11.8658 7.77748 11.8658 5.71428V3.61804H12.6639C12.94 3.61804 13.1639 3.39418 13.1639 3.11804C13.1639 2.8419 12.94 2.61804 12.6639 2.61804H11.6886Z" fill="#465A8B"/></svg>`,
          accessControl: {
            action: 'read',
            data: 'analitics-refund',
          },
        })
      }

      return _actions
    },
  },
  refundOperationId: REFUND,
  methods: {
    handleFullInfo(transactionData) {
      this.innerTransaction = new Transaction(transactionData)
    },
    async handleActionFired(action) {
      try {
        await action.handler()
      } catch (e) {
        console.error(e)
      }

      try {
        this.$refs.transactionItem.actionFinished(action.id)
      } catch (e) {}
    },

    async loadCallback() {
      let callbackResponse =
        await CallbackOperationsService.getCallbackInformationService(
          this.transaction.operationId
        )
      this.callbackInformation = callbackResponse
      console.log('callbackResponse: ', callbackResponse)
      if (callbackResponse.events.length === 0) {
        this.showCallback = true
      } else {
        this.showCallback = true
      }
    },

    async loadReceipt() {
      let receiptResponse = await CompaniesApi.getBillReceipt(
        this.selectedCompany,
        this.transaction.operationId
      )
      this.transactionReceiptBlob = URL.createObjectURL(receiptResponse.data)
      return this.$refs.transactionReceiptPopup.open()
    },

    async makeRefund() {
      await this.$router.push({
        name: 'refund-operation',
        params: {
          id: this.selectedCompany,
          operationId: this.transaction.operationId,
        },
      })
    },

    handleParentOperationIdClicked(parentOperationId) {
      $emit(this, 'parentOperationIdClicked', parentOperationId)
    },
  },
  emits: ['parentOperationIdClicked'],
}
</script>

<style lang="scss" scoped>
.analytics-table-item {
  .transaction-table-item {
    .transaction-table-item__actions-menu {
      right: 0;
      z-index: 111;
    }
    &__type {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #585f6d;
      opacity: 0.7;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      position: relative;

      &-refund {
        margin-top: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &__id {
      word-break: break-all;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #585f6d;
      opacity: 0.7;
    }

    &__amount {
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: #1a1f29;
      opacity: 0.7;
      justify-content: flex-end;
    }

    &__client {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      &-full-name {
        color: #1a1f29;

        &:empty {
          display: none;
        }
      }

      &-phone,
      &-email {
        color: #27365b;
        word-break: break-all;

        &:empty {
          display: none;
        }
      }
    }

    &__currency {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #585f6d;
      opacity: 0.7;
    }

    &__status {
      align-self: center;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 20px;
      padding-right: 20px;

      &-item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 105px;
        height: 28px;
        border-radius: 6px;
        font-weight: 500;
        font-size: 14px;
        line-height: 28px;

        &--paid {
          background: rgba(39, 204, 36, 0.2);
          color: #44d941;
        }
      }
    }
  }
}
</style>
